import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Payer, payersApi } from '../../../api/expenses';
import {
  CreateBankBalanceCheckDto,
  CreateFounderTransactionDto,
  FounderTransaction,
  TreasuryOverview,
  treasuryApi,
} from '../../../api/treasury';
import { useConfirm } from '../../../components/shared/ConfirmDialog';
import { UseFoundersReturn } from '../types';
import { getErrorMessage } from '@/lib/errors';

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyTransaction = (): CreateFounderTransactionDto => ({
  date: todayISO(),
  payerId: '',
  kind: 'reimbursement',
  amount: 0,
  comment: '',
});

const emptyBalance = (): CreateBankBalanceCheckDto => ({
  date: todayISO(),
  amount: 0,
  comment: '',
});

export function useFounders(): UseFoundersReturn {
  const confirm = useConfirm();
  const [overview, setOverview] = useState<TreasuryOverview | null>(null);
  const [payers, setPayers] = useState<Payer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransactionDialogOpen, setIsTransactionDialogOpen] = useState(false);
  const [isSharesDialogOpen, setIsSharesDialogOpen] = useState(false);
  const [isBalanceDialogOpen, setIsBalanceDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<FounderTransaction | null>(null);
  const [transactionForm, setTransactionForm] = useState<CreateFounderTransactionDto>(
    emptyTransaction(),
  );
  const [balanceForm, setBalanceForm] = useState<CreateBankBalanceCheckDto>(emptyBalance());

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [data, payersList] = await Promise.all([
        treasuryApi.getOverview(),
        payersApi.getAll(),
      ]);
      setOverview(data);
      setPayers(payersList);
    } catch (error: any) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить расчёты'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openCreateTransaction = () => {
    setEditingTransaction(null);
    setTransactionForm({
      ...emptyTransaction(),
      payerId: payers.find((p) => !p.is_company)?.id || '',
    });
    setIsTransactionDialogOpen(true);
  };

  const openEditTransaction = (transaction: FounderTransaction) => {
    setEditingTransaction(transaction);
    setTransactionForm({
      date: transaction.date,
      payerId: transaction.payer_id,
      kind: transaction.kind,
      amount: transaction.amount,
      comment: transaction.comment || '',
    });
    setIsTransactionDialogOpen(true);
  };

  const submitTransaction = async () => {
    if (isSubmitting) return;
    if (!transactionForm.payerId) {
      toast.error('Выберите основателя');
      return;
    }
    if (!transactionForm.amount || transactionForm.amount <= 0) {
      toast.error('Сумма должна быть больше 0');
      return;
    }
    setIsSubmitting(true);
    try {
      if (editingTransaction) {
        await treasuryApi.updateTransaction(editingTransaction.id, transactionForm);
        toast.success('Движение обновлено');
      } else {
        await treasuryApi.createTransaction(transactionForm);
        toast.success('Движение добавлено');
      }
      setIsTransactionDialogOpen(false);
      setEditingTransaction(null);
      fetchAll();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить движение'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteTransaction = async (id: string) => {
    const ok = await confirm({
      title: 'Удалить движение?',
      description: 'Касса, долг и прибыль пересчитаются.',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!ok) return;
    try {
      await treasuryApi.deleteTransaction(id);
      toast.success('Движение удалено');
      fetchAll();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось удалить движение'));
    }
  };

  const openBalanceCheck = () => {
    setBalanceForm(emptyBalance());
    setIsBalanceDialogOpen(true);
  };

  const submitBalanceCheck = async () => {
    if (isSubmitting) return;
    if (!balanceForm.date) {
      toast.error('Укажите дату выписки');
      return;
    }
    setIsSubmitting(true);
    try {
      await treasuryApi.createBalanceCheck(balanceForm);
      toast.success('Остаток записан');
      setBalanceForm(emptyBalance());
      setIsBalanceDialogOpen(false);
      fetchAll();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось записать остаток'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteBalanceCheck = async (id: string) => {
    const ok = await confirm({
      title: 'Удалить запись сверки остатка?',
      description: 'Сверка кассы с выпиской пропадёт из истории; расхождение пересчитается по оставшимся записям.',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!ok) return;
    try {
      await treasuryApi.deleteBalanceCheck(id);
      toast.success('Запись удалена');
      fetchAll();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось удалить запись'));
    }
  };

  const updateShare = async (payerId: string, profitShare: number) => {
    try {
      await payersApi.update(payerId, { profitShare });
      toast.success('Доля сохранена');
      fetchAll();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить долю'));
    }
  };

  return {
    overview,
    payers,
    isLoading,
    loadError,
    refresh: fetchAll,
    isSubmitting,
    isTransactionDialogOpen,
    setIsTransactionDialogOpen,
    isSharesDialogOpen,
    setIsSharesDialogOpen,
    isBalanceDialogOpen,
    setIsBalanceDialogOpen,
    editingTransaction,
    transactionForm,
    setTransactionForm,
    balanceForm,
    setBalanceForm,
    openCreateTransaction,
    openEditTransaction,
    submitTransaction,
    deleteTransaction,
    openBalanceCheck,
    submitBalanceCheck,
    deleteBalanceCheck,
    updateShare,
  };
}
